import { lazy, Suspense } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import { CsiProvider } from 'app/providers/csi/provider';
import { IbLayout } from 'app/widgets/main-layout';
import AlertIcon from '../components/Icons/AlertIcon';
import Loader from '../components/Loader';
import ROUTES, { ACCOUNTS_CARDS_ROUTES } from '../constants/routes';
import ExistingCustomerLayout from '../features/digital-onboarding/layout/existing-customer';
import OnboardingLayoutProvider from '../features/digital-onboarding/layout/onboarding.context';
import V1Redirect from '../features/transfer-v2/ui/v1-redirect';
import AniPayEditTemplate from '../pages/anipay/edit-template/page';
import { StateFormProvider } from '../pages/government-payment-portal/provider/StateFormProvider';
import ApplicationsPage from '../pages/salary-card/ui/applications/page';
import CardExtensionPage from '../pages/salary-card/ui/card-extension/page';
import PhoneNumbersPage from '../pages/salary-card/ui/card-extension/phone-numbers/page';
import ExtensionSuccessOperation from '../pages/salary-card/ui/card-extension/success-operation';
import NewApplication from '../pages/salary-card/ui/new-application';
import OrderPage from '../pages/salary-card/ui/order/page';
import SalaryCardPage from '../pages/salary-card/ui/page';

// Lazy imports
const AccountsCardsPage = lazy(() => import('../pages/accounts-cards'));
const AccountsRoute = lazy(() =>
  import('app/features/accounts-cards').then(module => ({ default: module.AccountsRoute }))
);
const CardsRoute = lazy(() =>
  import('app/features/accounts-cards').then(module => ({ default: module.CardsRoute }))
);
const NotFoundPage = lazy(() => import('../pages/NotFoundPage/NotFoundPage'));
const InProgressPage = lazy(() => import('app/pages/InProgressPage'));
const ComingSoon = lazy(() => import('app/pages/ComingSoon/ComingSoonPage'));
const NotHasAccess = lazy(() => import('app/pages/NotHasAccess/NotHasAccess'));
const EmployeeAddPage = lazy(() => import('app/pages/EmployeeAddPage'));
const TemplatesPage = lazy(() => import('app/pages/TemplatesPage'));
const SalaryPage = lazy(() => import('app/pages/SalaryPage'));
const Login = lazy(() => import('../pages/Authorization/Login'));
const Recovery = lazy(() => import('../pages/Authorization/Recovery'));
const EmailSent = lazy(() => import('../pages/Authorization/EmailSent'));
const NewPass = lazy(() => import('../pages/Authorization/NewPass'));
const ConfirmAsanImza = lazy(() => import('app/pages/Authorization/ConfirmAsanImza'));
const OTP = lazy(() => import('../pages/Authorization/OTP'));
const SelectCompany = lazy(() => import('../pages/Authorization/SelectCompany'));
const DocSign = lazy(() => import('../pages/Authorization/DocSign'));
const LinkConfirm = lazy(() => import('../pages/Authorization/LinkConfirm'));
const HomePage = lazy(() => import('../pages/main'));
const TransferPage = lazy(() => import('../pages/TransferPage'));
const PaymentOrdersPage = lazy(() => import('../pages/payment-orders'));
const SignedOperationsPage = lazy(() => import('../pages/SignedOperationsPage'));
const OldProfile = lazy(() => import('../pages/ClientProfilePage'));
const LastOperationsPage = lazy(() => import('app/pages/LastOperationsPage'));

// Digital Account related imports
const Register = lazy(() => import('../pages/register/register'));
const RegisterOtp = lazy(() => import('../pages/register/otp'));
const RegisterSelectCompany = lazy(() => import('../pages/register/select-company'));

// User profile
const ProfileRoot = lazy(() => import('../pages/profile/ui/layout'));
const CheckUserLayout = lazy(() => import('../pages/profile/ui/check-user-layout'));
const MainPage = lazy(() => import('../pages/profile/ui/info/page'));
const Users = lazy(() => import('../pages/profile/ui/users/page'));
const CreateUser = lazy(() => import('../pages/profile/ui/create-user/page'));
const UpdateUser = lazy(() => import('../pages/profile/ui/update-user/page'));

// Guarantee page
const GuaranteeOrder = lazy(() => import('../pages/guarantee-order/ui/page'));
const GuaranteeList = lazy(() => import('../pages/guarantee-list/ui/page'));
const GuaranteeV2 = lazy(() => import('../pages/guarantee-v2/ui/page'));
const GuaranteeListIncomings = lazy(
  () => import('../pages/guarantee-list/ui/guarantee-list.incomings')
);
const GuaranteeListOutgoings = lazy(
  () => import('../pages/guarantee-list/ui/guarantee-list.outgoings')
);

// Guarantee V2 - New Application
const NewApplicationLayout = lazy(() => import('../pages/guarantee-v2/ui/new-application/layout'));
const TenderDetailsStep = lazy(
  () => import('../pages/guarantee-v2/ui/new-application/steps/tender-details/TenderDetailsStep')
);
const OrderInfoStep = lazy(
  () => import('../pages/guarantee-v2/ui/new-application/steps/order-info/OrderInfoStep')
);
const DocumentUploadStep = lazy(
  () => import('../pages/guarantee-v2/ui/new-application/steps/document-upload/DocumentUploadStep')
);
const ConfirmationStep = lazy(
  () => import('../pages/guarantee-v2/ui/new-application/steps/confirmation/ConfirmationStep')
);
const SigningStep = lazy(
  () => import('../pages/guarantee-v2/ui/new-application/steps/signing/SigningStep')
);
const SuccessStep = lazy(
  () => import('../pages/guarantee-v2/ui/new-application/steps/success/SuccessStep')
);

// Loan pages
const LoanPage = lazy(() => import('../pages/loan/ui/page'));
const OfferConfirmationPage = lazy(() => import('../pages/loan/ui/offer-confirmation/page'));
const OfferCompletedPage = lazy(() => import('../pages/loan/ui/offer-completed/page'));
const OfferDeclinedPage = lazy(() => import('../pages/loan/ui/offer-declined/page'));

// Credit line pages
const CreditLinePage = lazy(() => import('../pages/credit-line/ui'));
const CreditLineDetailsPage = lazy(() => import('../pages/credit-line/ui/details/page'));
const CreditLineUsagePage = lazy(() => import('../pages/credit-line/ui/usage/page'));
const CreditLineUsageConfirmPage = lazy(() => import('../pages/credit-line/ui/usage/confirm/page'));
const CreditLineUsageSuccessPage = lazy(() => import('../pages/credit-line/ui/usage/success/page'));

// AniPay page
const AniPayRoot = lazy(() => import('../pages/anipay/ui/layout'));
const AniPayPage = lazy(() => import('../pages/anipay/ui/page'));
const AniPaySignUp = lazy(() => import('app/pages/anipay/sign-up/ui/page'));
const AniPayAliases = lazy(() => import('app/pages/anipay/alias/ui/page'));
const AniPayNewPayment = lazy(() => import('app/pages/anipay/new-payment/ui/page'));

// Login layout
const LoginWrapper = lazy(() => import('../components/Layouts/login-wrapper/ui/login-wrapper'));

// Government Payment Portal pages
const GovernmentPaymentPortalPage = lazy(
  () => import('../pages/government-payment-portal/pages/home/page')
);
const GovernmentCustomsCommitteePage = lazy(
  () => import('../pages/government-payment-portal/pages/customs-committee/page')
);
const CustomsComitteeSummaryPage = lazy(
  () => import('../pages/government-payment-portal/pages/summary/customs-committee')
);
const TaxServicePage = lazy(
  () => import('../pages/government-payment-portal/pages/tax-service/page')
);
const TaxServiceSummaryPage = lazy(
  () => import('../pages/government-payment-portal/pages/summary/tax-service')
);

const InsurancePage = lazy(() => import('../pages/government-payment-portal/pages/insurance/page'));

const InsuranceSummaryPage = lazy(
  () => import('../pages/government-payment-portal/pages/summary/insurance')
);

// PreFlight Fee
const PreFlightFeePage = lazy(() => import('../pages/preflight-fee/ui/page'));
const PreFlightFeePaymentPage = lazy(
  () => import('../pages/preflight-fee/ui/payment/payment-page')
);

const PreFlightFeeSummaryPage = lazy(
  () => import('../pages/preflight-fee/ui/summary/preflight-summary')
);

const TransferPageV2 = lazy(() => import('../pages/transfer-page-v2/ui/page'));

// Transfer v2 — foreign transfer
const ForeignTransferLayout = lazy(() => import('../features/transfer-v2/foreign/ui/layout'));
const ForeignTransferForm = lazy(() => import('../features/transfer-v2/foreign/ui/form'));
const ForeignTransferConfirm = lazy(() => import('../features/transfer-v2/foreign/ui/confirm'));

// Digital Account Redirect Pages

// Button Test Page
const ButtonTestPage = lazy(() => import('../pages/ButtonTestPage'));

// Fallback component for suspense
const SuspenseFallback = () => <Loader />;

// Create the router
export const registered_routes = [
  {
    path: '/',
    element: <Navigate to={'az/login'} replace />,
  },
  {
    path: '/',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <LoginWrapper />
      </Suspense>
    ),
    children: [
      {
        path: ROUTES.login,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <Login />
          </Suspense>
        ),
      },
      {
        path: ROUTES.recovery,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <Recovery />
          </Suspense>
        ),
      },
      {
        path: ROUTES.linkConfirm,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <LinkConfirm />
          </Suspense>
        ),
      },
      {
        path: ROUTES.linkSent,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <EmailSent />
          </Suspense>
        ),
      },
      {
        path: ROUTES.setNewPass,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <NewPass />
          </Suspense>
        ),
      },
      {
        path: ROUTES.confirmAsanImza,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <ConfirmAsanImza />
          </Suspense>
        ),
      },
      {
        path: ROUTES.otp,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <OTP />
          </Suspense>
        ),
      },
      {
        path: ROUTES.selectCompany,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <SelectCompany />
          </Suspense>
        ),
      },
      {
        path: ROUTES.docSign,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <DocSign />
          </Suspense>
        ),
      },
      {
        path: ROUTES.register,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <Register />
          </Suspense>
        ),
      },
      {
        path: ROUTES.registerOtp,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <RegisterOtp />
          </Suspense>
        ),
      },
      {
        path: ROUTES.registerSelectCompany,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <RegisterSelectCompany />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: '/',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <CsiProvider>
          <IbLayout />
        </CsiProvider>
      </Suspense>
    ),
    children: [
      {
        path: ROUTES.homePage,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <HomePage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.soon,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <ComingSoon />
          </Suspense>
        ),
      },
      {
        path: ROUTES.transferPage,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <V1Redirect target={'form'}>
              <TransferPage />
            </V1Redirect>
          </Suspense>
        ),
      },
      {
        path: ROUTES.foreignTransfer,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <ForeignTransferLayout />
          </Suspense>
        ),
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<SuspenseFallback />}>
                <ForeignTransferForm />
              </Suspense>
            ),
          },
          {
            path: 'confirm',
            element: (
              <Suspense fallback={<SuspenseFallback />}>
                <ForeignTransferConfirm />
              </Suspense>
            ),
          },
          {
            path: 'edit-template',
            element: (
              <Suspense fallback={<SuspenseFallback />}>
                <ForeignTransferForm />
              </Suspense>
            ),
          },
          {
            path: 'new-template',
            element: (
              <Suspense fallback={<SuspenseFallback />}>
                <ForeignTransferForm />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: ROUTES.transferPageV2,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <TransferPageV2 />
          </Suspense>
        ),
      },
      {
        path: ROUTES.paymentOrdersPage,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <PaymentOrdersPage />
          </Suspense>
        ),
      },
      {
        path: '/az/old-profile',
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <OldProfile />
          </Suspense>
        ),
      },
      {
        path: ROUTES.clientProfile,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <CheckUserLayout />
          </Suspense>
        ),
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<SuspenseFallback />}>
                <ProfileRoot />
              </Suspense>
            ),
            children: [
              {
                path: 'info',
                element: (
                  <Suspense fallback={<SuspenseFallback />}>
                    <MainPage />
                  </Suspense>
                ),
              },
              {
                path: 'users',
                element: (
                  <Suspense fallback={<SuspenseFallback />}>
                    <Users />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: 'create-user',
            element: (
              <Suspense fallback={<SuspenseFallback />}>
                <CreateUser />
              </Suspense>
            ),
          },
          {
            path: 'update-user/:employeeId',
            element: (
              <Suspense fallback={<SuspenseFallback />}>
                <UpdateUser />
              </Suspense>
            ),
          },
          {
            index: true,
            element: <Navigate to='info' />,
          },
        ],
      },
      {
        path: ROUTES.lastOperations,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <LastOperationsPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.guaranteeOrderPage,
        element: (
          <Suspense fallback={<Loader />}>
            <Outlet />
          </Suspense>
        ),
        children: [
          {
            index: true,
            element: <Navigate to={'order'} replace />,
          },
          {
            path: 'order',
            element: (
              <Suspense fallback={<SuspenseFallback />}>
                <GuaranteeOrder />
              </Suspense>
            ),
          },
          {
            path: 'list',
            element: (
              <Suspense fallback={<SuspenseFallback />}>
                <GuaranteeList />
              </Suspense>
            ),
            children: [
              {
                index: true,
                element: <Navigate to={'incomings'} replace />,
              },
              {
                path: 'incomings',
                element: (
                  <Suspense fallback={<SuspenseFallback />}>
                    <GuaranteeListIncomings />
                  </Suspense>
                ),
              },
              {
                path: 'outgoings',
                element: (
                  <Suspense fallback={<SuspenseFallback />}>
                    <GuaranteeListOutgoings />
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },
      {
        path: ROUTES.salaryCardPage,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <SalaryCardPage />
          </Suspense>
        ),
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<SuspenseFallback />}>
                <NewApplication />
              </Suspense>
            ),
          },
          {
            path: 'extension',
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<SuspenseFallback />}>
                    <CardExtensionPage />
                  </Suspense>
                ),
              },
              {
                path: 'phone-numbers',
                element: (
                  <Suspense fallback={<SuspenseFallback />}>
                    <PhoneNumbersPage />
                  </Suspense>
                ),
              },
              {
                path: 'success',
                element: (
                  <Suspense fallback={<SuspenseFallback />}>
                    <ExtensionSuccessOperation />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: 'applications',
            element: (
              <Suspense fallback={<SuspenseFallback />}>
                <ApplicationsPage />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: ROUTES.salaryCardOrder,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <OrderPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.guaranteeV2Page,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <Outlet />
          </Suspense>
        ),
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<SuspenseFallback />}>
                <GuaranteeV2 />
              </Suspense>
            ),
          },
          {
            path: 'new-application',
            element: (
              <Suspense fallback={<SuspenseFallback />}>
                <NewApplicationLayout />
              </Suspense>
            ),
            children: [
              {
                path: 'tender-details',
                element: (
                  <Suspense fallback={<SuspenseFallback />}>
                    <TenderDetailsStep />
                  </Suspense>
                ),
              },
              {
                path: 'order-info',
                element: (
                  <Suspense fallback={<SuspenseFallback />}>
                    <OrderInfoStep />
                  </Suspense>
                ),
              },
              {
                path: 'document-upload',
                element: (
                  <Suspense fallback={<SuspenseFallback />}>
                    <DocumentUploadStep />
                  </Suspense>
                ),
              },
              {
                path: 'confirmation',
                element: (
                  <Suspense fallback={<SuspenseFallback />}>
                    <ConfirmationStep />
                  </Suspense>
                ),
              },
              {
                path: 'signing',
                element: (
                  <Suspense fallback={<SuspenseFallback />}>
                    <SigningStep />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: 'new-application/success',
            element: (
              <Suspense fallback={<SuspenseFallback />}>
                <SuccessStep />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: ROUTES.loanPage,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <Outlet />
          </Suspense>
        ),
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<SuspenseFallback />}>
                <LoanPage />
              </Suspense>
            ),
          },
          {
            path: 'offer/:tx_fid/confirmation',
            element: (
              <Suspense fallback={<SuspenseFallback />}>
                <OfferConfirmationPage />
              </Suspense>
            ),
          },
          {
            path: 'offer/:tx_fid/completed',
            element: (
              <Suspense fallback={<SuspenseFallback />}>
                <OfferCompletedPage />
              </Suspense>
            ),
          },
          {
            path: 'offer/:tx_fid/declined',
            element: (
              <Suspense fallback={<SuspenseFallback />}>
                <OfferDeclinedPage />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: ROUTES.creditLine,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <CreditLinePage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.creditLineDetails,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <CreditLineDetailsPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.creditLineUsage,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <CreditLineUsagePage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.creditLineUsageConfirm,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <CreditLineUsageConfirmPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.creditLineUsageSuccess,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <CreditLineUsageSuccessPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.accountsCards,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <AccountsCardsPage />
          </Suspense>
        ),
        children: [
          { index: true, element: <Navigate to={ACCOUNTS_CARDS_ROUTES.accounts} replace /> },
          { path: ACCOUNTS_CARDS_ROUTES.accounts, element: <AccountsRoute /> },
          { path: ACCOUNTS_CARDS_ROUTES.cards, element: <CardsRoute /> },
        ],
      },
      {
        path: ROUTES.templatesPage,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <TemplatesPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.newTemplate,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <V1Redirect target={'new-template'}>
              <TransferPage />
            </V1Redirect>
          </Suspense>
        ),
      },
      {
        path: ROUTES.editTemplate,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <V1Redirect target={'edit-template'}>
              <TransferPage />
            </V1Redirect>
          </Suspense>
        ),
      },
      {
        path: ROUTES.signedOperations,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <SignedOperationsPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.salaryPage,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <SalaryPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.employeeAdd,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <EmployeeAddPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.aniPay,
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<SuspenseFallback />}>
                <AniPayRoot />
              </Suspense>
            ),
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<SuspenseFallback />}>
                    <AniPayPage />
                  </Suspense>
                ),
              },
              {
                path: 'sign-up',
                element: (
                  <Suspense fallback={<SuspenseFallback />}>
                    <AniPaySignUp />
                  </Suspense>
                ),
              },
              {
                path: 'alias/:dataType',
                element: (
                  <Suspense fallback={<SuspenseFallback />}>
                    <AniPayAliases />
                  </Suspense>
                ),
              },
              {
                path: 'new-payment',
                element: (
                  <Suspense fallback={<SuspenseFallback />}>
                    <AniPayNewPayment />
                  </Suspense>
                ),
              },
              {
                path: 'edit-template/:templateId',
                element: (
                  <Suspense fallback={<SuspenseFallback />}>
                    <AniPayEditTemplate />
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },
      {
        path: ROUTES.governmentPaymentPortal,
        children: [
          {
            path: '',
            element: (
              <StateFormProvider>
                <Outlet />
              </StateFormProvider>
            ),
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<SuspenseFallback />}>
                    <GovernmentPaymentPortalPage />
                  </Suspense>
                ),
              },
              {
                path: 'dgk',
                element: (
                  <Suspense fallback={<SuspenseFallback />}>
                    <GovernmentCustomsCommitteePage />
                  </Suspense>
                ),
              },
              {
                path: 'dgk/summary',
                element: (
                  <Suspense fallback={<SuspenseFallback />}>
                    <CustomsComitteeSummaryPage />
                  </Suspense>
                ),
              },
              {
                path: 'tax',
                element: (
                  <Suspense fallback={<SuspenseFallback />}>
                    <TaxServicePage />
                  </Suspense>
                ),
              },
              {
                path: 'tax/summary',
                element: (
                  <Suspense fallback={<SuspenseFallback />}>
                    <TaxServiceSummaryPage />
                  </Suspense>
                ),
              },
              {
                path: 'insurance',
                element: (
                  <Suspense fallback={<SuspenseFallback />}>
                    <InsurancePage />
                  </Suspense>
                ),
              },
              {
                path: 'insurance/summary',
                element: (
                  <Suspense fallback={<SuspenseFallback />}>
                    <InsuranceSummaryPage />
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },
      {
        path: ROUTES.preFlightFee,
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<SuspenseFallback />}>
                <Outlet />
              </Suspense>
            ),
          },
          {
            index: true,
            element: (
              <Suspense fallback={<SuspenseFallback />}>
                <PreFlightFeePage />
              </Suspense>
            ),
          },
          {
            path: 'payment',
            element: (
              <Suspense fallback={<SuspenseFallback />}>
                <PreFlightFeePaymentPage />
              </Suspense>
            ),
          },
          {
            path: 'summary',
            element: (
              <Suspense fallback={<SuspenseFallback />}>
                <PreFlightFeeSummaryPage />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: ROUTES.notHasAccess,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <NotHasAccess />
          </Suspense>
        ),
      },
      {
        path: ROUTES.buttonTest,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <ButtonTestPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.preflight,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <InProgressPage
              mainTextContent='Reysqabağı yoxlama ödənişi yaxın zamanda sizin üçün əlçatan olacaq.'
              secondaryTextContent='Bu bölmə, sərnişin və yük daşımalarında istifadə olunan avtonəqliyyat vasitələrinin reysqabağı texniki baxış ödənişi üçün nəzərdə tutulub.'
              showButton={false}
              showHeader={false}
              icon={AlertIcon}
              imagePath={'/images/rocket.svg'}
            />
          </Suspense>
        ),
      },
      {
        path: ROUTES.onboarding,
        element: <Outlet />,
        children: [
          {
            element: (
              <OnboardingLayoutProvider>
                <Outlet />
              </OnboardingLayoutProvider>
            ),
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<SuspenseFallback />}>
                    <ExistingCustomerLayout />
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },
      // In-progress routes
      ...[ROUTES.cardOrder, ROUTES.depositsPage, ROUTES.ordersPage].map(path => ({
        path,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <InProgressPage />
          </Suspense>
        ),
      })),
    ],
  },
  {
    path: '*',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <NotFoundPage />
      </Suspense>
    ),
  },
];
